// Assumption classification: two discrete PM-owned dimensions (importance ×
// certainty) with the risk level derived from them. One source of truth, because
// step 7's fragile-assumption logic and the deck both read the stored
// `riskLevel` and drift here would silently change what the pivot-or-persevere
// review shows.
//
// IMPORTED BY THE ai-assumption LAMBDA (`lambda/shared/assumption-ai.ts`,
// `lambda/ai-assumption/index.ts`) via a relative path. Keep this file
// dependency-free — no React, no @xyflow/react, no NodeMetadata import — or the
// frontend gets bundled into the Lambda.

export const IMPORTANCE_VALUES = ['high', 'low'] as const;
export const CERTAINTY_VALUES = ['known', 'unknown'] as const;
export const RISK_LEVELS = ['critical', 'risky', 'fine'] as const;

export type Importance = (typeof IMPORTANCE_VALUES)[number];
export type Certainty = (typeof CERTAINTY_VALUES)[number];
export type RiskLevel = (typeof RISK_LEVELS)[number];

// The assumption-node metadata fields this module reads. A structural subset of
// the frontend's NodeMetadata (which we can't import — see the header note).
export interface AssumptionMetaFields {
  importance?: Importance;
  certainty?: Certainty;
  riskLevel?: RiskLevel;
  aiFlagged?: 'impossible';
  impossibleDismissed?: boolean;
  dismissReason?: string;
  downgradeReason?: string;
}

// Anything the PM hasn't tested yet is a risk; how big a risk depends on how
// much rides on it. Certainty collapses both importances to 'fine' — a known
// quantity isn't a risk whatever it's worth.
export function deriveRiskLevel(importance: Importance, certainty: Certainty): RiskLevel {
  if (certainty === 'known') return 'fine';
  return importance === 'high' ? 'critical' : 'risky';
}

// Places an assumption that predates the two dimensions: the inverse of
// deriveRiskLevel, picking the quadrant each risk level most plausibly came
// from. 'fine' is ambiguous (both importances derive it) and resolves to
// important-and-known, the more useful reading of a PM who said "this is fine".
// The PM's first drag replaces the guess with a real value; there is no
// migration.
export function dimensionsFromRisk(riskLevel?: string): { importance: Importance; certainty: Certainty } {
  if (riskLevel === 'critical') return { importance: 'high', certainty: 'unknown' };
  if (riskLevel === 'fine') return { importance: 'high', certainty: 'known' };
  // 'risky', plus missing/garbled: default to the risky quadrant rather than
  // anything that reads as safe.
  return { importance: 'low', certainty: 'unknown' };
}

// The board's read path. Stored dimensions win; a legacy assumption is reverse-
// placed from its risk level.
export function assumptionDimensions(meta?: AssumptionMetaFields): {
  importance: Importance;
  certainty: Certainty;
} {
  const importance = meta?.importance;
  const certainty = meta?.certainty;
  if (
    importance !== undefined &&
    certainty !== undefined &&
    (IMPORTANCE_VALUES as readonly string[]).includes(importance) &&
    (CERTAINTY_VALUES as readonly string[]).includes(certainty)
  ) {
    return { importance, certainty };
  }
  return dimensionsFromRisk(meta?.riskLevel);
}

// The board's write path. The node PUT replaces metadata wholesale, so this
// returns the whole object with the classification fields swapped and everything
// else (the AI's flag and reasoning, dismissal and downgrade history) carried
// through.
export function withDimensions<T extends Record<string, unknown>>(
  meta: T | undefined,
  importance: Importance,
  certainty: Certainty,
): T & { importance: Importance; certainty: Certainty; riskLevel: RiskLevel } {
  return {
    ...((meta ?? {}) as T),
    importance,
    certainty,
    riskLevel: deriveRiskLevel(importance, certainty),
  };
}

// An *active* impossible blocker: the AI flagged it and the PM hasn't dismissed
// that call. The flag is a permanent AI record; dismissing it is a separate,
// reversible PM action.
export function isImpossibleBlocker(meta?: AssumptionMetaFields): boolean {
  return meta?.aiFlagged === 'impossible' && meta?.impossibleDismissed !== true;
}
